import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 120000, // 2 minute timeout for AI processing
});

// Store for the Clerk getToken function (set by ClerkTokenProvider)
let clerkGetToken = null;

// Function to set the Clerk token getter (called from React component)
export const setClerkTokenGetter = (getToken) => {
  clerkGetToken = getToken;
};

// Request interceptor for auth
api.interceptors.request.use(async (config) => {
  // Try to get Clerk token if available
  if (clerkGetToken) {
    try {
      const token = await clerkGetToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to get Clerk token:', error);
    }
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ============ Dataset Management ============

export const uploadVoterData = async (file, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);

  if (options.datasetName) {
    formData.append('dataset_name', options.datasetName);
  }
  if (options.description) {
    formData.append('description', options.description);
  }
  if (options.fieldMapping) {
    formData.append('field_mapping', JSON.stringify(options.fieldMapping));
  }
  if (options.addressConfig) {
    formData.append('address_config', JSON.stringify(options.addressConfig));
  }
  if (options.visualizationConfig) {
    formData.append('visualization_config', JSON.stringify(options.visualizationConfig));
  }
  // Default location for geocoding
  if (options.defaultCity) {
    formData.append('default_city', options.defaultCity);
  }
  if (options.defaultState) {
    formData.append('default_state', options.defaultState);
  }
  if (options.defaultCounty) {
    formData.append('default_county', options.defaultCounty);
  }
  // Geographic outlier rejection
  if (options.rejectOutliers) {
    formData.append('reject_outliers', 'true');
    if (options.outlierThreshold) {
      formData.append('outlier_threshold', options.outlierThreshold.toString());
    }
  }

  const response = await api.post('/election/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: options.onProgress
  });

  return response.data;
};

export const getDatasets = async () => {
  const response = await api.get('/election/datasets');
  return response.data;
};

export const getDataset = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}`);
  return response.data;
};

export const deleteDataset = async (datasetId) => {
  const response = await api.delete(`/election/datasets/${datasetId}`);
  return response.data;
};

// ============ Schema Analysis ============

export const analyzeSchema = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/election/analyze-schema', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return response.data;
};

export const analyzeUnstructuredData = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/election/analyze-unstructured', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return response.data;
};

// ============ Voter Data ============

export const getVoters = async (datasetId, options = {}) => {
  const params = new URLSearchParams();

  if (options.limit) params.append('limit', options.limit);
  if (options.offset) params.append('offset', options.offset);
  if (options.filter) params.append('filter', JSON.stringify(options.filter));

  const response = await api.get(`/election/datasets/${datasetId}/voters?${params}`);
  return response.data;
};

export const searchVoters = async (datasetId, query) => {
  const response = await api.post(`/election/datasets/${datasetId}/search`, { query });
  return response.data;
};

// ============ Geocoding ============

export const geocodeAddresses = async (datasetId, addressConfig) => {
  const response = await api.post(`/election/datasets/${datasetId}/geocode`, {
    address_config: addressConfig
  });

  return response.data;
};

export const getGeocodingStatus = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/geocode/status`);
  return response.data;
};

// ============ Visualization ============

export const getVisualizationSuggestions = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/visualization-suggestions`);
  return response.data;
};

export const saveVisualizationConfig = async (datasetId, config) => {
  const response = await api.post(`/election/datasets/${datasetId}/visualization-config`, config);
  return response.data;
};

// ============ Statistics ============

export const getDatasetStats = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/stats`);
  return response.data;
};

export const getFieldDistribution = async (datasetId, fieldName) => {
  const response = await api.get(`/election/datasets/${datasetId}/distribution/${fieldName}`);
  return response.data;
};

// ============ Export ============

export const exportDataset = async (datasetId, format = 'csv') => {
  const response = await api.get(`/election/datasets/${datasetId}/export`, {
    params: { format },
    responseType: 'blob'
  });

  return response.data;
};

// ============ Health Check ============

export const healthCheck = async () => {
  const response = await api.get('/election/health');
  return response.data;
};

// ============ Configuration ============

export const getConfiguration = async () => {
  const response = await api.get('/config/');
  return response.data;
};

export const getConfigSchema = async () => {
  const response = await api.get('/config/schema');
  return response.data;
};

export const updateConfiguration = async (section, updates) => {
  const response = await api.put('/config/', { section, updates });
  return response.data;
};

export const getAvailableModels = async () => {
  const response = await api.get('/config/models/available');
  return response.data;
};

export const getCurrentModel = async () => {
  const response = await api.get('/config/models/current');
  return response.data;
};

export const switchModel = async (provider, modelId) => {
  const response = await api.post('/config/models/switch', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const validateModel = async (provider, modelId) => {
  const response = await api.post('/config/models/validate', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const clearModelCache = async () => {
  const response = await api.post('/config/models/cache/clear');
  return response.data;
};

// ============ Route Optimization ============

export const optimizeRoutes = async (datasetId, options) => {
  const response = await api.post(`/election/datasets/${datasetId}/optimize-routes`, options);
  return response.data;
};

export const getRoutes = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/routes`);
  return response.data;
};

export const getRoute = async (routeId) => {
  const response = await api.get(`/election/routes/${routeId}/full`);
  return response.data;
};

export const startRoute = async (routeId) => {
  const response = await api.post(`/election/routes/${routeId}/start`);
  return response.data;
};

export const completeRoute = async (routeId) => {
  const response = await api.post(`/election/routes/${routeId}/complete`);
  return response.data;
};

export const createRoute = async (datasetId, routeData) => {
  const response = await api.post(`/election/datasets/${datasetId}/routes`, routeData);
  return response.data;
};

export const getCheckInUpdates = async (datasetId, since = null) => {
  const params = since ? { since } : {};
  const response = await api.get(`/election/datasets/${datasetId}/check-ins/updates`, { params });
  return response.data;
};

export const exportRoute = async (routeId, format = 'pdf') => {
  const response = await api.get(`/election/routes/${routeId}/export`, {
    params: { format },
    responseType: 'blob'
  });
  return response.data;
};

// ============ Natural Language Visualization ============

export const generateVisualization = async (datasetId, prompt) => {
  const response = await api.post(`/election/datasets/${datasetId}/visualize`, { prompt });
  return response.data;
};

// ============ Canvasser Check-in ============

export const checkInVoter = async (voterId, checkInData) => {
  // Extract dataset_id from the check-in data or use a default
  const datasetId = checkInData.dataset_id || 'default';
  const response = await api.post(`/election/datasets/${datasetId}/voters/${voterId}/check-in`, checkInData);
  return response.data;
};

export const getVoterCheckIn = async (datasetId, voterId) => {
  const response = await api.get(`/election/datasets/${datasetId}/voters/${voterId}/check-in`);
  return response.data;
};

export const getRouteProgress = async (routeId) => {
  const response = await api.get(`/election/routes/${routeId}/progress`);
  return response.data;
};

export const getDatasetProgress = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/check-ins`);
  return response.data;
};

export const exportCheckIns = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/check-ins/export`, {
    responseType: 'blob'
  });
  return response.data;
};

// ============ Voter Management ============

export const getVoter = async (datasetId, voterId) => {
  const response = await api.get(`/election/datasets/${datasetId}/voters/${voterId}`);
  return response.data;
};

export const updateVoter = async (datasetId, voterId, updates) => {
  const response = await api.put(`/election/datasets/${datasetId}/voters/${voterId}`, updates);
  return response.data;
};

export const addVoter = async (datasetId, voterData) => {
  const response = await api.post(`/election/datasets/${datasetId}/voters`, voterData);
  return response.data;
};

// ============ Canvassing Configuration ============

export const getCanvassingConfig = async (datasetId) => {
  const response = await api.get(`/election/datasets/${datasetId}/canvassing-config`);
  return response.data;
};

export const updateCanvassingConfig = async (datasetId, config) => {
  const response = await api.put(`/election/datasets/${datasetId}/canvassing-config`, config);
  return response.data;
};

// ============ Custom Fields ============

export const getCustomFields = async () => {
  const response = await api.get('/election/custom-fields');
  return response.data;
};

export const createCustomField = async (fieldData) => {
  const response = await api.post('/election/custom-fields', fieldData);
  return response.data;
};

export const updateCustomField = async (fieldId, updates) => {
  const response = await api.put(`/election/custom-fields/${fieldId}`, updates);
  return response.data;
};

export const deleteCustomField = async (fieldId) => {
  const response = await api.delete(`/election/custom-fields/${fieldId}`);
  return response.data;
};

export const reorderCustomFields = async (fieldIds) => {
  const response = await api.post('/election/custom-fields/reorder', fieldIds);
  return response.data;
};

export default api;
